import { Injectable } from '@angular/core';

import { ReplaySubject, Subject } from 'rxjs';

import { FilterService, FilterSet } from 'app/modules/collection/services/filter.service';
import { WidgetChartOptions } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import { HighchartsOptions } from 'app/modules/widget/services/chart-display.service';
import { Comparison, Widget } from 'app/providers/bridge-api/dashboard.service';
import { ComparisonType } from '../components/create-comparison-drawer/create-comparison-drawer.component';

export const DEFAULT_COMPARISON_COLOR = {
  red: 37,
  green: 55,
  blue: 77,
  alpha: 1,
};
export const DEFAULT_COMPARISON_COLOR_STRING = 'rgba(37,55,77,1)';

@Injectable({ providedIn: 'root' })
export class ComparisonHandlerService {
  readonly arithmeticOperatorDictionary = {
    '>': 'Greater than',
    '<': 'Less than',
    '>=': 'Greater or Equals',
    '<=': 'Less or Equals',
    '=': 'Equals',
    '!=': 'Not Equals',
  };

  private currentPageComparisons: Comparison[] = [];

  private comparisonListChangeSub = new ReplaySubject<Comparison[]>();
  private comparisonApplySub = new Subject<{ comparison; widgetId }>();
  private comparisonRevokeSub = new Subject<{ widgetId; revokedComparison }>();

  readonly comparisonListChanged$ = this.comparisonListChangeSub.asObservable();
  readonly comparisonApplied$ = this.comparisonApplySub.asObservable();
  readonly comparisonRevoked$ = this.comparisonRevokeSub.asObservable();

  constructor(private filterService: FilterService) {}

  loadComparisons(comparisonsByKey: { [key: string]: Comparison }, pageId) {
    this.currentPageComparisons = Object.values(comparisonsByKey).filter((c) => c.page === pageId);

    this.comparisonListChangeSub.next(this.currentPageComparisons);
  }

  addComparison(comparison) {
    this.currentPageComparisons.push(comparison);

    this.comparisonListChangeSub.next([...this.currentPageComparisons]);
  }

  updateComparison(comparison) {
    const idx = this.currentPageComparisons.findIndex((c) => c.id === comparison.id);
    if (idx > -1) {
      this.currentPageComparisons.splice(idx, 1, comparison);

      this.comparisonListChangeSub.next([...this.currentPageComparisons]);

      comparison.widgets.forEach((w) => this.comparisonApplySub.next({ comparison, widgetId: w }));
    }
  }

  deleteComparison(comparison) {
    this.currentPageComparisons = this.currentPageComparisons.filter((c) => c.id !== comparison.id);

    comparison.widgets.forEach((w) =>
      this.comparisonRevokeSub.next({ widgetId: w, revokedComparison: comparison })
    );

    this.comparisonListChangeSub.next(this.currentPageComparisons);
  }

  applyComparison(comparison, widget, isActivityBarAction = false) {
    if (widget.payload.comparison === comparison.id) {
      // we need this here so when we clone a widget we update the comparison pills on the activity bar
      if (isActivityBarAction) {
        comparison.widgets.push(widget.id);
        this.comparisonApplySub.next({ comparison, widgetId: widget.id });
      }
      return;
    }

    const previousComparison = this.currentPageComparisons.find(
      (c) => c.id === widget.payload.comparison
    );

    if (previousComparison) {
      this.revokeComparison(previousComparison, widget);
    }

    widget.payload.comparison = comparison.id;
    comparison.widgets.push(widget.id);

    this.comparisonApplySub.next({ comparison, widgetId: widget.id });
  }

  revokeComparison(comparison, widget, triggerRevokeSub = true) {
    delete widget.payload.comparison;
    comparison.widgets = comparison.widgets.filter((w) => w !== widget.id);

    if (triggerRevokeSub) {
      this.comparisonRevokeSub.next({ widgetId: widget.id, revokedComparison: comparison });
    }
  }

  filterDisplayValueExtractorFn(type: ComparisonType): (set: FilterSet) => string {
    switch (type) {
      case 'date':
        return (filter) => {
          if (filter.label) {
            return filter.label;
          }

          const label = this.filterService.computeLabelForDateFilter(
            filter.and[0].value,
            'xiShort'
          );
          if (filter.and[0].key === 'daterange') {
            return label.replace('to', '-');
          }

          return label;
        };
      case 'keyword':
        return (filter) => {
          if (filter.label) {
            return filter.label;
          }

          if (filter.and.length) {
            return `Contains "${filter.and[0].value}"`;
          }
          return `Does not contain "${filter.not[0].value}"`;
        };
      case 'numeric':
        return (filter) => {
          if (filter.label) {
            return filter.label;
          }

          const modelValue = this.filterService.numericRangeFilterToModel(filter.and[0].value);

          let value = `${this.arithmeticOperatorDictionary[modelValue.leftOperand.operator]} "${
            modelValue.leftOperand.value
          }"`;

          if (modelValue.booleanOperator) {
            value = `${value} ${modelValue.booleanOperator} ${
              this.arithmeticOperatorDictionary[modelValue.rightOperand.operator]
            } "${modelValue.rightOperand.value}"`;
          }

          return value;
        };
      case 'sectionFilters':
        return (filter) => {
          if (filter.label) {
            return filter.label;
          }

          return `Equal to "${filter.and[0].value}"`;
        };
    }
  }

  setChartColors(
    widget: Widget,
    comparison: Comparison,
    chartOptions: HighchartsOptions
  ): HighchartsOptions {
    if (!comparison?.filters.length) {
      return chartOptions;
    }

    switch (widget.payload.type) {
      case 'numericMetadata':
        const chartPayload = widget.payload as WidgetChartOptions;
        if (chartPayload.numericMetadataFields?.length === 1) {
          for (let index = 0; index < comparison.filters.length; index++) {
            chartOptions.series[index].color =
              comparison.filters[index].color ?? DEFAULT_COMPARISON_COLOR_STRING;
          }
        }
        break;
      case 'documentCount':
        if (
          widget.payload.aggregator !== 'time' &&
          widget.payload.aggregator !== 'percentage_time'
        ) {
          for (let index = 0; index < comparison.filters.length; index++) {
            chartOptions.series[index].color =
              comparison.filters[index].color ?? DEFAULT_COMPARISON_COLOR_STRING;
            chartOptions.series[index].data.forEach((item) => delete item.color);
          }
        }
        break;
      default:
        break;
    }

    return chartOptions;
  }

  getComparison(id: string) {
    return this.currentPageComparisons.find((c) => c.id === id);
  }
}
